import clsx from 'clsx';
import { MouseEvent, useState } from 'react';
// Analytics
import {
  COPY_QUOTATION_CLICK,
  ITEM_CLICKED,
  MODIFY_VIN_CLICK,
  SEE_DETAILS_CLICK,
  THREE_DOTS_MENU,
  trackEvent,
} from 'src/analytics';
// Components
import {
  faCar,
  faCopy,
  faEdit,
  faEye,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Menu,
  MenuProps,
  makeStyles,
  withStyles,
} from '@material-ui/core';
import { MoreHoriz } from '@material-ui/icons';
// design system
import { colors } from 'design-system';
import MenuOptionItem from './MenuOptionItem';

const StyledMenu = withStyles({
  paper: {
    borderRadius: '8px',
    border: '1px solid #EAEAEA',
    boxShadow: '0px 2px 15px rgba(0, 0, 0, 0.15)',
    backgroundColor: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#FFFFFF',
    },
  },
})((props: MenuProps) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
));

const reportClickEvent = (element: string) => {
  trackEvent({
    eventName: THREE_DOTS_MENU,
    eventProperties: {
      [ITEM_CLICKED]: element,
    },
  });
};

const useStyles = makeStyles(() => ({
  iconButton: {
    color: colors.bright_turquoise,
    width: '40px',
    height: '27px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: '#F1FAFF',
    },
  },
  alterBackground: {
    backgroundColor: '#F3F3FF',
  },
}));

type Props = {
  onModificateVin: () => void;
  quotationId: number;
  onShowDocuments: () => void;
  onCopyQuotation: () => void;
  disableDownload: boolean;
  showVinOption: boolean;
  isEmitted: boolean;
};

const MenuButtonContent = ({
  onModificateVin,
  quotationId,
  onShowDocuments,
  onCopyQuotation,
  showVinOption,
  isEmitted,
}: Props): JSX.Element => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleVinModification = () => {
    reportClickEvent(MODIFY_VIN_CLICK);
    onModificateVin();
    setAnchorEl(null);
  };

  const handleCopyQuotation = () => {
    reportClickEvent(COPY_QUOTATION_CLICK);
    onCopyQuotation();
    setAnchorEl(null);
  };

  const handleShowDocumentst = () => {
    reportClickEvent(SEE_DETAILS_CLICK);
    onShowDocuments();
    setAnchorEl(null);
  };

  return (
    <Box>
      <Box
        aria-label={`quotation-menu-button-${quotationId}`}
        aria-controls={`quotation-menu-${quotationId}`}
        aria-haspopup="true"
        className={clsx(
          classes.iconButton,
          anchorEl ? classes.alterBackground : '',
        )}
        onClick={handleClick}
        data-testid="menu-button"
      >
        <MoreHoriz />
      </Box>
      <StyledMenu
        id={`quotation-menu-${quotationId}`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        data-testid="menu-component"
      >
        {isEmitted && showVinOption && (
          <MenuOptionItem
            data-testid="modify-vin-menu-item"
            onClick={handleVinModification}
            optionText="modificar vin"
          >
            <FontAwesomeIcon
              icon={faCar}
              fontSize={16}
              color={colors.bright_turquoise}
            />
          </MenuOptionItem>
        )}
        {isEmitted && (
          <MenuOptionItem
            onClick={(): void => {}}
            optionText="añadir información adicional"
            data-testid="add-information-menu-item"
          >
            <FontAwesomeIcon
              icon={faCar}
              fontSize={16}
              color={colors.bright_turquoise}
            />
          </MenuOptionItem>
        )}
        {isEmitted && (
          <MenuOptionItem
            onClick={handleShowDocumentst}
            optionText="ver documentos"
            data-testid="show-documents-menu-item"
          >
            <FontAwesomeIcon
              icon={faEye}
              fontSize={16}
              color={colors.bright_turquoise}
            />
          </MenuOptionItem>
        )}

        <MenuOptionItem
          onClick={handleCopyQuotation}
          optionText={isEmitted ? 'Copiar cotización' : 'Editar cotización'}
          data-testid="quotation-copy-menu-item"
        >
          <FontAwesomeIcon
            icon={isEmitted ? faCopy : faEdit}
            fontSize={16}
            color={colors.bright_turquoise}
          />
        </MenuOptionItem>
      </StyledMenu>
    </Box>
  );
};

export default MenuButtonContent;
